import { useEffect, useMemo, useState } from "react";
import { planCap } from "../../../server/src/lib/cap-plan";
import { Link } from "react-router-dom";
import { useMeta } from "../lib/meta";
import { api } from "../lib/api";
import { formatINR, formatPoints } from "../lib/format";
import type { CapProgress } from "../lib/types";
import { Panel, ProgressBar } from "./ui";

/** "15,000 bonus pts", or "₹2,00,000" for a cap on spend. */
function amount(cap: CapProgress, value: number): string {
  if (cap.type === "SPEND") return formatINR(value, { compact: value >= 100000 });
  return `${formatPoints(value)}${cap.appliesTo === "BONUS" ? " bonus" : ""} pts`;
}

/**
 * Accelerated earning that is capped — SmartBuy's 10X, a category's 5X —
 * and where each cap stands this period: how much has been earned against
 * it, how much is left, and roughly how much more spend each capped rate
 * will take before the rest earns only the base rate.
 *
 * Loads itself and stays out of the way when there are no caps, or when it
 * cannot load (the offline demo has no such endpoint).
 */
export function CapTracker({ className = "", compact = false }: { className?: string; compact?: boolean }) {
  const [caps, setCaps] = useState<CapProgress[] | null>(null);

  useEffect(() => {
    api
      .get<{ caps: CapProgress[] }>("/points/caps")
      // A cap that only limits base-rate earning is not accelerated earning:
      // shown once something has counted against it, not before.
      .then((r) => setCaps(r.caps.filter((c) => c.accelerated || c.used > 0)))
      .catch(() => setCaps([]));
  }, []);

  if (!caps || caps.length === 0) return null;

  return (
    <Panel
      title="Accelerated earning caps"
      subtitle="What each capped rate has earned against its cap this period, and how much more it can earn before the cap is reached"
      className={className}
    >
      <ul className="space-y-5">
        {caps.map((cap) => {
          const full = cap.remaining <= 0;
          return (
            <li key={`${cap.cardId}:${cap.key}`}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink-900">
                    <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: cap.cardColor }} aria-hidden />
                    {cap.name}
                    <span className="font-normal text-ink-500"> · {cap.cardName}</span>
                  </p>
                  <p className="truncate text-xs text-ink-500">
                    {cap.windowLabel} · {cap.daysLeft} day{cap.daysLeft === 1 ? "" : "s"} left
                    {cap.appliesTo === "BONUS" ? " · base points are never capped" : ""}
                  </p>
                </div>
                <span className="tnum shrink-0 text-xs text-ink-500">
                  {amount(cap, cap.used)} / {amount(cap, cap.limit)}
                </span>
              </div>
              <div className="mt-2">
                <ProgressBar pct={cap.pct} color={full ? "#b54708" : cap.cardColor} />
              </div>
              <p className={`mt-1.5 text-xs ${full ? "text-amber-800" : "text-ink-600"}`}>
                {full ? (
                  <>
                    Cap reached — until it resets,{" "}
                    {cap.appliesTo === "BONUS"
                      ? "these earn only the base rate"
                      : cap.overflow === "NOTHING"
                        ? "these earn nothing"
                        : "the rest earns the base rate"}
                    .
                  </>
                ) : (
                  <>
                    <span className="font-medium text-ink-900">{amount(cap, cap.remaining)}</span> left
                    {!compact && cap.appliesTo === "BONUS" && cap.basePointsAlongside > 0 && (
                      <span className="text-ink-500"> · {formatPoints(cap.basePointsAlongside)} base pts earned alongside</span>
                    )}
                  </>
                )}
              </p>
              {cap.needsBaseRate && (
                <p className="mt-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                  {cap.cardName} has no base rate, so rules set as a multiple of it earn nothing. Set one under{" "}
                  <Link to="/cards" className="font-medium underline">Cards &amp; rules</Link> → Edit.
                </p>
              )}
              {cap.perDay && (
                <p className="mt-1 text-xs text-ink-500">
                  Also no more than {amount(cap, cap.perDay.limit)} a day — today {amount(cap, cap.perDay.usedToday)} used,{" "}
                  <span className="font-medium text-ink-800">{amount(cap, cap.perDay.remainingToday)}</span> left.
                </p>
              )}
              {!compact && <CapBreakdown cap={cap} unit={(n) => amount(cap, n)} />}
              {!full && <CapCategories cap={cap} compact={compact} />}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

/** One line per category a cap covers: a rule with three categories is three lines. */
function categoryLines(cap: CapProgress, labelOf: (key: string) => string) {
  return cap.rules.flatMap((rule) => {
    const where = [
      rule.merchantPatterns.length > 0 ? `at ${rule.merchantPatterns[0].split("+").join(" + ")}` : null,
      rule.channel === "ONLINE" ? "online" : rule.channel === "OFFLINE" ? "in store" : null,
    ]
      .filter(Boolean)
      .join(" ");
    const categories = rule.categories.length > 0 ? rule.categories : [null];
    return categories.map((category) => ({
      key: `${rule.ruleId}:${category ?? "*"}`,
      label: `${category ? labelOf(category) : rule.name}${where ? ` ${where}` : ""}`,
      rateLabel: rule.rateLabel,
      draw: rule.draw,
      sub: rule.subCap ? { key: rule.subCap.key, remaining: rule.subCap.remaining } : null,
      subName: rule.subCap?.name ?? null,
    }));
  });
}

/**
 * What each rule under a cap has drawn from it this period — hotels, flights,
 * each brand partner — and every sub-cap inside it, so a shared cap is seen
 * part by part.
 */
function CapBreakdown({ cap, unit }: { cap: CapProgress; unit: (n: number) => string }) {
  const subCaps = [...new Map(cap.rules.filter((r) => r.subCap).map((r) => [r.subCap!.key, r.subCap!])).values()];
  const earning = cap.rules.filter((r) => r.earned > 0);
  if (cap.rules.length < 2 && subCaps.length === 0) return null;
  return (
    <div className="mt-2 rounded-md bg-ink-50 px-2.5 py-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-ink-500">So far this period</p>
      {earning.length === 0 ? (
        <p className="mt-1 text-xs text-ink-500">Nothing drawn from the cap yet.</p>
      ) : (
        <ul className="mt-1 space-y-0.5 text-xs">
          {earning.map((r) => (
            <li key={r.ruleId} className="flex justify-between gap-3">
              <span className="text-ink-700">{r.name}</span>
              <span className="tnum text-ink-800">{unit(r.earned)}</span>
            </li>
          ))}
        </ul>
      )}
      {subCaps.map((sub) => (
        <div key={sub.key} className="mt-2">
          <div className="flex justify-between gap-3 text-xs">
            <span className="text-ink-700">{sub.name} — its own cap within this one</span>
            <span className="tnum text-ink-500">
              {unit(sub.used)} / {unit(sub.limit)}
            </span>
          </div>
          <div className="mt-1">
            <ProgressBar pct={sub.limit > 0 ? Math.min(100, (sub.used / sub.limit) * 100) : 0} color={sub.remaining <= 0 ? "#b54708" : cap.cardColor} />
          </div>
          <p className="mt-0.5 text-[11px] text-ink-500">
            {sub.remaining > 0 ? `${unit(sub.remaining)} left, as long as the shared cap has room` : "Used up — these spends earn only the base rate until it resets"}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * How much more each category under a shared cap can take — alone, and as
 * part of a mix you plan: spend on one category leaves less for the others.
 */
function CapCategories({ cap, compact }: { cap: CapProgress; compact: boolean }) {
  const meta = useMeta();
  const labelOf = (key: string) => meta?.categories.find((c) => c.key === key)?.label ?? key;
  const lines = categoryLines(cap, labelOf).filter((l) => l.draw);
  const [planning, setPlanning] = useState(false);
  const [planned, setPlanned] = useState<Record<string, string>>({});

  const plan = useMemo(
    () =>
      planCap(
        cap.remaining,
        lines,
        Object.fromEntries(Object.entries(planned).map(([k, v]) => [k, Number(v) || 0])),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cap.remaining, planned, cap.key],
  );

  if (lines.length === 0) return null;
  const unit = (n: number) => (cap.type === "SPEND" ? formatINR(n) : `${formatPoints(n)}${cap.appliesTo === "BONUS" ? " bonus" : ""} pts`);
  const anyPlanned = Object.values(planned).some((v) => Number(v) > 0);

  return (
    <div className="mt-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-ink-400">
            <th className="py-1 pr-2 font-normal">{lines.length > 1 ? "Each category" : "Category"}</th>
            <th className="py-1 pr-2 font-normal">Uses the cap at</th>
            {planning && <th className="py-1 pr-2 text-right font-normal">You plan to spend</th>}
            <th className="py-1 text-right font-normal">{planning && anyPlanned ? "Room after your plan" : "Room if all spent here"}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const p = plan.lines.find((l) => l.key === line.key);
            return (
              <tr key={line.key} className="border-t border-ink-100">
                <td className="py-1 pr-2 text-ink-700">
                  {line.label}
                  {line.subName && <span className="block text-[11px] text-ink-400">within {line.subName}'s own cap</span>}
                  {planning && p && p.overSub > 0 && (
                    <span className="block text-[11px] text-amber-700">{unit(p.overSub)} past its own cap — no bonus on that part</span>
                  )}
                </td>
                <td className="py-1 pr-2 text-ink-500">{line.rateLabel}</td>
                {planning && (
                  <td className="py-1 pr-2 text-right">
                    <input
                      className="input tnum w-28 px-2 py-1 text-right text-xs"
                      type="number"
                      min={0}
                      step={1000}
                      placeholder="₹0"
                      aria-label={`Planned spend on ${line.label}`}
                      value={planned[line.key] ?? ""}
                      onChange={(e) => setPlanned({ ...planned, [line.key]: e.target.value })}
                    />
                  </td>
                )}
                <td className="tnum py-1 text-right font-medium text-ink-800">
                  {p?.roomAfter == null ? "no limit" : `${formatINR(p.roomAfter)} more`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-1 text-xs text-ink-500">
        {planning
          ? "Each planned amount is counted as one transaction, as the bank counts it: in whole units, rounded down."
          : lines.length > 1
            ? "Each figure is the spend, in one transaction, that uses up what is left — if it all goes to that category. Spend on one leaves less for the others."
            : "The spend, in one transaction, that uses up what is left."}{" "}
        {!planning && "Split over several transactions, a little more can fit, since each is rounded down to whole units."}
      </p>
      {!compact && lines.length > 1 && (
        <div className="mt-1">
          <button className="text-xs text-accent-700 underline" onClick={() => { setPlanning((v) => !v); setPlanned({}); }}>
            {planning ? "Close the plan" : "Plan a mix"}
          </button>
          {planning && anyPlanned && (
            <p className={`mt-1 text-xs ${plan.over > 0 ? "text-amber-800" : "text-ink-600"}`}>
              {plan.over > 0 ? (
                <>
                  That plan needs {unit(plan.used)} of the cap, but only {unit(cap.remaining)} {cap.type === "SPEND" ? "is" : "are"} left —{" "}
                  {unit(plan.over)} over.
                  The spend past it earns {cap.appliesTo === "BONUS" ? "only the base rate" : cap.overflow === "NOTHING" ? "nothing" : "the base rate"}.
                </>
              ) : (
                <>
                  That plan uses {unit(plan.used)} of the {unit(cap.remaining)} left, leaving{" "}
                  <span className="font-medium text-ink-900">{unit(plan.left)}</span> — the room shown above.
                </>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
